import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { api } from '../api/client';

const DEFAULT_POLL_MS = 4000;

/**
 * Polls everything one session page needs, behind a generation guard.
 *
 * The bug this exists for: the page used to call a wholesale `load()` both from
 * a 4s interval and directly after every mutation, with no guard, no abort and
 * no catch. A poll issued before an answer committed but resolving after it
 * would overwrite the answered request with its stale `pending` snapshot — and
 * since the input had already been cleared, the user's typed answer was gone.
 *
 * Three properties fix that class of bug, and they only work if they live in
 * exactly one place:
 *   1. every setState of a load is gated on that load still being the newest;
 *   2. `invalidate()` is called BEFORE a mutation, so any in-flight poll is
 *      already stale by the time it lands;
 *   3. `refreshNow()` restarts the interval, so the post-mutation refetch is
 *      not immediately followed by a stale timer poll.
 */
export function useSessionData(sessionId, pollMs = DEFAULT_POLL_MS) {
  const [data, setData] = useState({
    session: null, transcript: null, tasks: [], goals: [], artifacts: [], finalAnswer: null, syncedAt: null,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const genRef = useRef(0);
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (abortRef.current && !abortRef.current.signal.aborted) return;
    const gen = ++genRef.current;
    clearTimeout(timerRef.current);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const opts = { signal: ctrl.signal };

    try {
      const [session, transcript, tasks, goals, artifacts] = await Promise.all([
        api.getSession(sessionId, opts),
        api.getTranscript(sessionId, opts),
        api.listTasks(sessionId, opts),
        api.listAgentGoals(sessionId, opts),
        api.listArtifacts(sessionId, opts),
      ]);

      let finalAnswer = null;
      if (session.status === 'finished' && session.final_answer_id) {
        finalAnswer = await api.getFinalAnswer(sessionId, opts).catch(() => null);
      }

      if (gen !== genRef.current) return;   // stale — touch no state at all
      setData({ session, transcript, tasks, goals, artifacts, finalAnswer, syncedAt: new Date().toISOString() });
      setError(null);
    } catch (e) {
      if (gen !== genRef.current || axios.isCancel?.(e) || e?.name === 'CanceledError') return;
      setError(e);
    } finally {
      if (gen === genRef.current) {
        setLoading(false);
        abortRef.current = null;
        timerRef.current = setTimeout(load, pollMs);
      }
    }
  }, [sessionId, pollMs]);

  // Call before a mutation: everything currently in flight becomes stale.
  const invalidate = useCallback(() => {
    ++genRef.current;
    abortRef.current?.abort();
  }, []);

  const refreshNow = useCallback(() => {
    clearTimeout(timerRef.current);
    return load();
  }, [load, pollMs]);

  useEffect(() => {
    load();
    return () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
      ++genRef.current;                    // late responses after unmount are stale
    };
  }, [load, pollMs]);

  return { ...data, error, loading, reload: load, invalidate, refreshNow };
}
